/**
 * Import stĺpov zo súboru.
 *
 * Prepísané 2026-09-30. Pôvodne to vedelo len jeden formát — WKT export
 * z Google MyMaps `POINT (lon lat), názov, popis` — a hlavičku rozpoznávalo
 * podľa toho, či prvý riadok obsahuje „kt" alebo „point". Obyčajné
 * `lat,lon,názov` tak zlyhalo na každom riadku.
 *
 * Teraz:
 *  - WKT `POINT (lon lat)` v ktoromkoľvek stĺpci
 *  - samostatné stĺpce so šírkou a dĺžkou, rozpoznané podľa hlavičky
 *  - bez hlavičky: prvé dve čísla ako šírka, dĺžka (dá sa prehodiť)
 *  - hlavička sa pozná tak, že sa z prvého riadku nedajú prečítať súradnice
 *  - stredník aj tabulátor ako oddeľovač (Excel v slovenskom prostredí)
 */

export interface ImportedPole {
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
}

export interface ImportResult {
  poles: ImportedPole[];
  errors: string[];
  /** Ako sa súbor prečítal — ukáže sa v dialógu, nech je vidno, čo sa stalo */
  format?: string;
}

/** „POINT (18.32 48.20)" -> { longitude, latitude }. WKT má najprv dĺžku. */
function parsePoint(pointStr: string): { longitude: number; latitude: number } | null {
  const m = /point\s*\(\s*(-?\d+(?:[.,]\d+)?)\s+(-?\d+(?:[.,]\d+)?)\s*\)/i.exec(pointStr || '');
  if (!m) return null;
  const longitude = parseFloat(m[1].replace(',', '.'));
  const latitude = parseFloat(m[2].replace(',', '.'));
  if (Number.isNaN(longitude) || Number.isNaN(latitude)) return null;
  return { longitude, latitude };
}

/** Rozdelí riadok podľa oddeľovača, rešpektuje polia v úvodzovkách. */
function parseLine(line: string, sep: string): string[] {
  const fields: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      // zdvojená úvodzovka vnútri poľa
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === sep && !inQuotes) {
      fields.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  fields.push(cur.trim());
  return fields;
}

/** Oddeľovač určíme podľa toho, ktorý sa v súbore vyskytuje najčastejšie. */
function detectSeparator(text: string): string {
  const vzorka = text.split('\n').slice(0, 5).join('\n');
  const kandidati = [',', ';', '\t'];
  let najlepsi = ',';
  let najviac = -1;
  for (const k of kandidati) {
    const n = vzorka.split(k).length;
    if (n > najviac) { najviac = n; najlepsi = k; }
  }
  return najlepsi;
}

function cislo(raw: string): number | null {
  if (raw === undefined || raw === null) return null;
  const v = parseFloat(String(raw).trim().replace(',', '.'));
  return Number.isNaN(v) ? null : v;
}

/** Nájde v hlavičke indexy stĺpcov so šírkou, dĺžkou, názvom a popisom. */
function mapColumns(header: string[]): {
  lat: number; lon: number; name: number; desc: number;
} {
  const h = header.map((x) => x.toLowerCase());
  const najdi = (vzory: string[]) =>
    h.findIndex((x) => vzory.some((v) => x.includes(v)));
  return {
    lat: najdi(['latitude', 'lat', 'sirka', 'šírka', 'y']),
    lon: najdi(['longitude', 'lon', 'lng', 'dlzka', 'dĺžka', 'x']),
    name: najdi(['name', 'nazov', 'názov', 'meno', 'popis stlpa']),
    desc: najdi(['description', 'popis', 'note', 'poznamka', 'poznámka']),
  };
}

/**
 * Prečíta CSV/TXT. `swapLatLon` prehodí šírku a dĺžku — pri súboroch bez
 * hlavičky sa nedá spoľahlivo uhádnuť, ktoré číslo je ktoré (na Slovensku
 * sú oba rozsahy podobné), takže to rieši prepínač v dialógu.
 */
export async function importPolesFromCSV(
  file: File,
  swapLatLon = false,
): Promise<ImportResult> {
  const text = await file.text();
  return parseCsvText(text, swapLatLon);
}

export function parseCsvText(text: string, swapLatLon = false): ImportResult {
  const poles: ImportedPole[] = [];
  const errors: string[] = [];

  if (!text || !text.trim()) {
    return { poles, errors: ['Súbor je prázdny.'] };
  }

  const sep = detectSeparator(text);
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);

  // Hlavička = riadok, z ktorého sa nedajú prečítať súradnice.
  const prvy = parseLine(lines[0], sep);
  const prvyMaWkt = prvy.some((f) => parsePoint(f) !== null);
  const prvyMaDvojicuCisel =
    prvy.filter((f) => cislo(f) !== null).length >= 2;
  const maHlavicku = !prvyMaWkt && !prvyMaDvojicuCisel;

  const stlpce = maHlavicku ? mapColumns(prvy) : { lat: -1, lon: -1, name: -1, desc: -1 };
  const start = maHlavicku ? 1 : 0;
  let pouzityFormat = '';

  for (let i = start; i < lines.length; i++) {
    const cisloRiadku = i + 1;
    const f = parseLine(lines[i], sep);

    // 1) WKT v ktoromkoľvek stĺpci
    let lat: number | null = null;
    let lon: number | null = null;
    let wktIndex = -1;
    for (let j = 0; j < f.length; j++) {
      const p = parsePoint(f[j]);
      if (p) { lat = p.latitude; lon = p.longitude; wktIndex = j; break; }
    }
    if (wktIndex >= 0) pouzityFormat = pouzityFormat || 'WKT POINT (Google MyMaps)';

    // 2) stĺpce podľa hlavičky
    if (lat === null && stlpce.lat >= 0 && stlpce.lon >= 0) {
      lat = cislo(f[stlpce.lat]);
      lon = cislo(f[stlpce.lon]);
      if (lat !== null && lon !== null) {
        pouzityFormat = pouzityFormat || 'stĺpce podľa hlavičky';
      }
    }

    // 3) bez hlavičky — prvé dve čísla
    if (lat === null) {
      const cisla: number[] = [];
      const indexy: number[] = [];
      for (let j = 0; j < f.length && cisla.length < 2; j++) {
        const v = cislo(f[j]);
        if (v !== null) { cisla.push(v); indexy.push(j); }
      }
      if (cisla.length === 2) {
        lat = cisla[0];
        lon = cisla[1];
        pouzityFormat = pouzityFormat || 'prvé dve čísla ako šírka a dĺžka';
      }
    }

    if (lat === null || lon === null) {
      errors.push(`Riadok ${cisloRiadku}: nenašiel som súradnice — „${lines[i].slice(0, 60)}"`);
      continue;
    }

    if (swapLatLon) { const t = lat; lat = lon; lon = t; }

    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      errors.push(`Riadok ${cisloRiadku}: súradnice mimo rozsahu (${lat}, ${lon})`
        + (Math.abs(lat) > 90 ? ' — nie sú prehodené šírka a dĺžka?' : ''));
      continue;
    }

    // Názov: z hlavičky, inak prvé textové pole, ktoré nie je WKT ani číslo
    let nazov = '';
    if (stlpce.name >= 0) nazov = f[stlpce.name] || '';
    if (!nazov) {
      nazov = f.find((x, j) =>
        j !== wktIndex && x && cislo(x) === null && parsePoint(x) === null) || '';
    }
    nazov = nazov.trim();

    let popis = '';
    if (stlpce.desc >= 0) popis = (f[stlpce.desc] || '').trim();

    poles.push({
      name: nazov || `Stĺp ${cisloRiadku}`,
      latitude: lat,
      longitude: lon,
      description: popis || undefined,
    });
  }

  return { poles, errors, format: pouzityFormat || 'neznámy' };
}

/**
 * KMZ je zazipovaný KML. Rozbaliť zip v prehliadači by si vyžadovalo knižnicu
 * (jszip), ktorú projekt nemá — a nepridávam ju pre funkciu, ktorá doteraz
 * bola nedostupná. KML sa načíta priamo, bez závislosti.
 */
export async function importPolesFromKMZ(file: File): Promise<ImportResult> {
  return {
    poles: [],
    errors: [
      'KMZ je zabalený KML archív a ten sa tu rozbaliť nedá. '
      + 'Premenuj súbor na .zip, rozbaľ ho a nahraj .kml, ktorý je vnútri. '
      + '(Alebo v Google Earth exportuj priamo ako .kml.)',
    ],
    format: 'KMZ (nepodporované)',
  };
}

/** KML Placemark -> stĺp. Súradnice sú v KML v poradí dĺžka,šírka[,výška]. */
export function parseKmlText(kml: string): ImportResult {
  const poles: ImportedPole[] = [];
  const errors: string[] = [];
  const doc = new DOMParser().parseFromString(kml, 'text/xml');
  const placemarks = Array.from(doc.getElementsByTagName('Placemark'));

  if (placemarks.length === 0) {
    return { poles, errors: ['V KML nie sú žiadne Placemark prvky.'], format: 'KML' };
  }

  placemarks.forEach((pm, idx) => {
    const coordEl = pm.getElementsByTagName('coordinates')[0];
    if (!coordEl || !coordEl.textContent) {
      errors.push(`Placemark ${idx + 1}: chýbajú súradnice`);
      return;
    }
    // Berieme len prvý bod — čiary a plochy nás nezaujímajú.
    const prvy = coordEl.textContent.trim().split(/\s+/)[0];
    const [lonS, latS] = prvy.split(',');
    const lon = cislo(lonS);
    const lat = cislo(latS);
    if (lat === null || lon === null) {
      errors.push(`Placemark ${idx + 1}: súradnice sa nedajú prečítať („${prvy}")`);
      return;
    }
    const nameEl = pm.getElementsByTagName('name')[0];
    const descEl = pm.getElementsByTagName('description')[0];
    poles.push({
      name: (nameEl?.textContent || '').trim() || `Stĺp ${idx + 1}`,
      latitude: lat,
      longitude: lon,
      description: (descEl?.textContent || '').trim() || undefined,
    });
  });

  return { poles, errors, format: 'KML / KMZ' };
}

/** Prečíta súbor podľa prípony. */
export async function importPolesFromFile(
  file: File,
  swapLatLon = false,
): Promise<ImportResult> {
  const n = file.name.toLowerCase();
  if (n.endsWith('.kmz')) return importPolesFromKMZ(file);
  if (n.endsWith('.kml')) return parseKmlText(await file.text());
  return importPolesFromCSV(file, swapLatLon);
}
